import { useState } from 'react';
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Coffee,
  Info,
  LogOut,
  MessageSquare,
} from 'react-feather';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { swalMessage } from '@components/common';
import ChangeModal from '@components/modal/changeModal';
import DetailsModal from '@components/modal/detailsModal';
import MessageSendModal from '@components/modal/messageSendModal';
import { getMapViewList, getSeatLogList, getSeminarItemList, userList } from '@lib/api';
import { blockTicket, forceExitTicket, reenterTicket, restTicket } from '@lib/api/v2/ticket';
import dayjs from '@lib/dayjs';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import { media } from '@styles/mediaQuery';
import { asArray, firstArray } from '@util/array';
import { checkIfCurrentFloor, swalReturnWarning } from '@util/commonFunctions';
import Format from '@util/formatter';
import { isExitedFixedSeat } from '@util/seatTicketCompatibility';
import { TICKET_CONFIRM_MESSAGES, TICKET_SUCCESS_MESSAGES } from '@util/ticketMessages';
import styled from 'styled-components';

const Btns = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  row-gap: 8px;
  margin-top: 10px;
`;
export const ButtonS = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 24%;
  padding: 8px 12px;
  border-radius: 4px;
  border: none;
  background-color: ${(props) => props.bg};
  color: ${(props) => (props.color ? props.color : '#fff')};
  font-weight: 600;
  font-size: 15px;
  > svg {
    height: 17px;
  }
  ${media.mobile`
    padding: 6px 10px;
    font-size: 13px;
    > svg {
      height: 15px;
    }
  `}
`;

const HistorySection = styled.div`
  margin-top: 16px;
  border-top: 1px solid #e9e9e9;
  padding-top: 12px;
`;

const HistoryToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  background-color: #f8fcff;
  border: 1px solid #e9e9e9;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #0f2a4a;
  cursor: pointer;

  > svg {
    height: 16px;
  }
`;

const HistoryTable = styled.table`
  width: 100%;
  margin-top: 10px;
  font-size: 13px;
  border-collapse: collapse;

  th,
  td {
    padding: 6px 8px;
    border-bottom: 1px solid #eee;
    text-align: left;
  }

  th {
    background-color: #f5f7fa;
    font-weight: 600;
  }

  tbody tr:hover {
    background-color: #fafafa;
  }
`;

const HistoryEmpty = styled.div`
  margin-top: 10px;
  padding: 16px;
  text-align: center;
  color: #888;
  font-size: 13px;
`;

const typeLabel = (t) => {
  switch (t) {
    case 1:
      return '일회권';
    case 2:
      return '기간권';
    case 3:
      return '시간정액권';
    case 4:
      return '세미나실';
    case 5:
      return '사물함';
    default:
      return '-';
  }
};

export const ModalBackground = styled.div`
  background-color: rgba(23, 36, 34, 0.42);
  backdrop-filter: blur(3px);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  z-index: 400;
`;

const CustomModalStyle = `
  .choice-modal-backdrop {
    z-index: 400 !important;
  }
  .choice-modal {
    z-index: 450 !important;
  }
`;

const ChoiceModal = ({ isOpen, onClose, seatData, setLists, lists, floorNum }) => {
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();
  const [changeSeatModal, setChangeSeatModal] = useState(false);
  const [detailUserModal, setDetailUserModal] = useState(false);
  const [isSendMsg, setIsSendMsg] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [detailTarget, setDetailTarget] = useState(null);

  const openDetailWith = (target) => {
    setDetailTarget(target);
    setDetailUserModal(true);
  };

  const handleHistoryPhoneClick = async (phoneNum) => {
    if (!phoneNum || phoneNum === '-') return;
    try {
      const res = await createRequest(userList, seatData.merchantId, phoneNum);
      const user = firstArray(res?.data)[0];
      if (user) openDetailWith(user);
    } catch (error) {
      catchError(error);
    }
  };
  const showChangeSeatModal = () => {
    setChangeSeatModal(!changeSeatModal);
  };

  const showDetailModal = () => {
    setDetailUserModal(!detailUserModal);
  };
  const toggleIsSendMsg = () => {
    setIsSendMsg(!isSendMsg);
  };

  const loadHistory = async () => {
    if (historyLoaded || historyLoading) return;
    setHistoryLoading(true);
    try {
      const start = dayjs().subtract(6, 'day').format('YYYY-MM-DD');
      const end = dayjs().format('YYYY-MM-DD');
      const { data } = await createRequest(getSeatLogList, seatData.merchantId, start, end);
      const all = Array.isArray(data?.result) ? data.result : [];
      const seatId = seatData.id;
      const rows = all
        .filter((item) => item.seatId === seatId)
        .map((item) => ({
          dateTime: dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss'),
          message: item.message || (item.subtype?.includes('exit') ? '퇴장' : ''),
          phoneNum: item.phoneNum || '-',
          ticketLabel: typeLabel(item.ticketType),
          ticketId: item.ticketId,
        }));
      setHistoryRows(rows);
      setHistoryLoaded(true);
    } catch (error) {
      catchError(error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next) loadHistory();
  };

  const outTicket = () => {
    const nowTime = dayjs();
    const diffTime = dayjs.duration(nowTime.diff(seatData.endDate)).asMilliseconds();
    if (diffTime > 0) {
      return swalReturnWarning(
        '이미 만료되어 퇴장처리가 불가능한 이용자입니다.\n초과결제 취소가 필요한 경우 퇴장여부의 초과결제 취소를 눌러 주세요.',
      );
    }
    // 맵뷰어 좌석 데이터의 세미나 type은 문자열 'seminar'로 내려온다(좌석 status 병합 시 고정).
    // 숫자 4만 비교하면 세미나 분기가 항상 누락되므로 둘 다 허용한다.
    const isSeminar = seatData.type === 4 || seatData.type === 'seminar';
    if (isExitedFixedSeat(seatData)) {
      return swalReturnWarning(
        '이미 일반 퇴장한 고정석입니다.\n좌석 계약을 끝내려면 완전퇴장을 눌러 주세요.',
      );
    }
    // 세미나실은 시간 예약제라 회원이 예약 시간보다 일찍 퇴장해도(isUsing!==1) 좌석은 예약 종료시각까지
    // 점유 표시로 남는다. 이때는 실제 입장 상태가 아니므로 강제 퇴장 대상이 아니고, 예약 종료 시 자동으로
    // 빈자리가 된다. 막다른 에러('사용 중인 이용권만...') 대신 상황을 안내한다.
    if (isSeminar && seatData.isUsing !== 1) {
      return swalReturnWarning(
        '이미 퇴장한 예약입니다.\n세미나실은 예약 시간이 종료되면 자동으로 빈자리로 바뀝니다.',
      );
    }
    const message = isSeminar
      ? '퇴장처리를 하시겠습니까?\n세미나실의 경우, 예약취소되어 현황에서도 사라집니다.'
      : '퇴장처리를 하시겠습니까?';
    swalMessage('warning', message, '퇴장', true, handleForceExit, undefined, '취소');
  };

  const handleForceExit = async () => {
    try {
      const ticketId = seatData.type === 4 ? seatData.id : seatData.ticketId;
      if (seatData.type === 4)
        await createRequest(blockTicket, { ticketId, block: 1 }); // 세미나실: 퇴장 + 차단 (예약 취소)
      else await createRequest(forceExitTicket, { ticketId }); // 일반 좌석: 퇴장만

      swalMessage('success', '퇴장처리가 완료되었습니다.');
      getSeatListApi();
      onClose();
    } catch (error) {
      catchError(error, '퇴장처리 중 에러가 발생했습니다. 다시 시도해 주세요.');
    }
  };

  // LETMEUP-619: 고정석 기간권 전용 "완전퇴장" — 일반 퇴장(handleForceExit)은 고정석의
  // tb_seat.status 를 rest 로만 바꾸고 idle 로 완전히 비우지 않는다(계약 유지 목적).
  // 이 버튼은 이용권 계약을 종료(block=1)하며 좌석을 즉시 다른 회원에게 개방한다.
  // seatData.status 가 using/rest 어느 쪽이어도 동작 — 이미 rest 로 멈춘 좌석 복구도 가능.
  const outFullTicket = () => {
    swalMessage('warning', TICKET_CONFIRM_MESSAGES.FULL_EXIT, '완전퇴장', true, handleFullExit, undefined, '취소');
  };

  const handleFullExit = async () => {
    try {
      await createRequest(blockTicket, { ticketId: seatData.ticketId, block: 1 });
      swalMessage('success', TICKET_SUCCESS_MESSAGES.FULL_EXIT);
      getSeatListApi();
      onClose();
    } catch (error) {
      catchError(error, '완전퇴장 처리 중 에러가 발생했습니다. 다시 시도해 주세요.');
    }
  };

  // LETMEUP-522: 관리자가 회원을 대신 외출(rest) 처리/복귀시키는 토글.
  // 세미나실(type=4)은 시간 예약제라 외출 개념이 없어 백엔드도 거부하므로 버튼 자체를 노출하지 않는다.
  const handleRest = async () => {
    try {
      await createRequest(restTicket, { ticketId: seatData.ticketId });
      swalMessage('success', '외출 처리가 완료되었습니다.');
      getSeatListApi();
      onClose();
    } catch (error) {
      catchError(error, '외출 처리 중 에러가 발생했습니다. 다시 시도해 주세요.');
    }
  };

  const handleReenter = async () => {
    try {
      await createRequest(reenterTicket, { ticketId: seatData.ticketId });
      swalMessage('success', '외출 복귀 처리가 완료되었습니다.');
      getSeatListApi();
      onClose();
    } catch (error) {
      catchError(error, '외출 복귀 처리 중 에러가 발생했습니다. 다시 시도해 주세요.');
    }
  };

  const restOrReenter = () => {
    if (seatData.status === 'rest') {
      swalMessage('warning', '외출을 종료하고 복귀 처리하시겠습니까?', '복귀', true, handleReenter, undefined, '취소');
    } else {
      swalMessage('warning', '외출 처리하시겠습니까?\n좌석은 계속 점유된 상태로 유지됩니다.', '외출', true, handleRest, undefined, '취소');
    }
  };

  const getSeatListApi = async () => {
    try {
      const res = await createRequest(getMapViewList, seatData.merchantId);

      const dateRange = Format.formatDateRange(new Date(), new Date());
      const seminarRes = await createRequest(
        getSeminarItemList,
        seatData.merchantId,
        dateRange.start,
        dateRange.end,
      );
      const usingSeminars = asArray(seminarRes?.data).filter((seminar) => seminar.isUsing);
      /**
       * 세미나실 입장 현황이 표시되지 않는 현상 임시 수정
       * Note: 세미나실 이용 정보를 추가 fetch 후 기존 좌석 목록에 합치는 방식을 사용하였습니다.
       * 차후 좌석 목록 조회 API가 수정되면 Array.prototype.map() 부분은 삭제되어야 합니다.
       */
      const seatList = asArray(res?.data)
        .filter(
          (item) =>
            (item.type === 'seat' || item.type === 'seminar') &&
            checkIfCurrentFloor(item, floorNum),
        )
        .map((seat) => {
          try {
            if (seat.type !== 'seminar') return seat;
            const filtered = usingSeminars.filter((seminar) => seminar.seminarId === seat.id);
            if (filtered.length > 0)
              return {
                ...seat,
                ...filtered[0],
                id: seat.id,
                ticketId: filtered[0].id,
                status: 'using',
                type: 'seminar',
              };
            return seat;
          } catch (error) {
            return seat;
          }
        });
      setLists(seatList);
      // if (floorNum) {
      //   setLists(
      //     res.data
      //       .filter((item) => item.type === 'seat' || item.type === 'seminar')
      //       .filter((list) => list.floor === +floorNum),
      //   );
      // } else {
      //   setLists(res.data.filter((item) => item.type === 'seat' || item.type === 'seminar'));
      // }
    } catch (error) {
      catchError(error);
    }
  };
  return (
    <>
      <style>{CustomModalStyle}</style>
      {detailUserModal && (
        <DetailsModal
          isOpen={detailUserModal}
          close={() => {
            setDetailUserModal(false);
            setDetailTarget(null);
          }}
          choiceModalClose={onClose}
          seat={detailTarget || seatData}
          zIndex={detailTarget ? 1500 : 450}
        />
      )}
      {changeSeatModal && (
        <ChangeModal
          isOpen={changeSeatModal}
          close={showChangeSeatModal}
          choiceModalClose={onClose}
          seat={seatData}
          setLists={setLists}
          lists={lists}
        />
      )}
      {isSendMsg && (
        <MessageSendModal
          isOpen={isSendMsg}
          onClose={toggleIsSendMsg}
          phoneNum={seatData.phoneNum}
          mid={seatData.merchantId}
        />
      )}
      <Modal
        centered
        isOpen={isOpen}
        toggle={onClose}
        zIndex={450}
        modalClassName="choice-modal"
        backdropClassName="choice-modal-backdrop">
        <ModalHeader className="modal-title" toggle={onClose}>
          {seatData.type !== 4 ? (
            <>
              {seatData.name ? `[${seatData.name}] ` : ''}
              {seatData.number}번 좌석
            </>
          ) : (
            '세미나실 이용'
          )}{' '}
          회원 정보
        </ModalHeader>
        <ModalBody>
          <h5>{seatData.phoneNum}</h5>
          <Btns>
            {seatData.type !== 4 &&
              seatData.type !== 'seminar' &&
              !isExitedFixedSeat(seatData) && (
              <ButtonS bg=" #FFC42B" color="#0F2A4A" onClick={showChangeSeatModal}>
                좌석이동 <ArrowRight />
              </ButtonS>
            )}

            <ButtonS bg="#21D7D9" color="#0F2A4A" onClick={toggleIsSendMsg}>
              문자발송 <MessageSquare />
            </ButtonS>
            <ButtonS bg="#722DE2 " onClick={showDetailModal}>
              상세정보 <Info />
            </ButtonS>
            {seatData.type !== 4 && seatData.type !== 'seminar' && (
              <ButtonS bg="#FF8A00" onClick={restOrReenter}>
                {seatData.status === 'rest' ? '외출복귀' : '외출'} <Coffee />
              </ButtonS>
            )}
            {!isExitedFixedSeat(seatData) && (
              <ButtonS bg="#F53C14" onClick={outTicket}>
                퇴장 <LogOut />
              </ButtonS>
            )}
            {seatData.type !== 4 && seatData.type !== 'seminar' && seatData.seatType === 1 && (
              <ButtonS bg="#8A1207" onClick={outFullTicket}>
                완전퇴장 <LogOut />
              </ButtonS>
            )}
          </Btns>
          <HistorySection>
            <HistoryToggle type="button" onClick={toggleHistory}>
              <span>최근 7일 이용 이력</span>
              {historyOpen ? <ChevronUp /> : <ChevronDown />}
            </HistoryToggle>
            {historyOpen &&
              (historyLoading ? (
                <HistoryEmpty>불러오는 중...</HistoryEmpty>
              ) : historyRows.length === 0 ? (
                <HistoryEmpty>최근 7일 이용 이력이 없습니다.</HistoryEmpty>
              ) : (
                <HistoryTable>
                  <thead>
                    <tr>
                      <th>일시</th>
                      <th>상태</th>
                      <th>전화번호</th>
                      <th>이용권</th>
                      <th>권번</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.map((row, idx) => (
                      <tr key={`${row.ticketId}-${row.dateTime}-${idx}`}>
                        <td>{row.dateTime}</td>
                        <td>{row.message}</td>
                        <td
                          style={{
                            color: '#0f2a4a',
                            cursor: row.phoneNum && row.phoneNum !== '-' ? 'pointer' : 'default',
                            textDecoration:
                              row.phoneNum && row.phoneNum !== '-' ? 'underline' : 'none',
                          }}
                          onClick={() => handleHistoryPhoneClick(row.phoneNum)}>
                          {row.phoneNum}
                        </td>
                        <td>{row.ticketLabel}</td>
                        <td>{row.ticketId ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </HistoryTable>
              ))}
          </HistorySection>
        </ModalBody>
      </Modal>
      <ModalBackground />
    </>
  );
};

export default ChoiceModal;
